// SPDX-License-Identifier: AGPL-3.0-only

import { requestJson } from "./http.js";

export type DateTimeFormatPreference = "browser" | "iso_local" | "de_24h" | "us_12h" | "date_only";
export type DashboardDefaultPreset = "live" | "today" | "this_week" | "this_month" | "last_month" | "this_year" | "last_year";

export interface UserSettings {
  readonly dateTimeFormat: DateTimeFormatPreference;
  readonly dashboardDefaultPreset: DashboardDefaultPreset;
  readonly dashboardDefaultResolution: string;
  readonly updatedAt: string | null;
}

export interface DateTimeFormatOption {
  readonly value: DateTimeFormatPreference;
  readonly label: string;
  readonly description: string;
}

export const DATE_TIME_FORMAT_OPTIONS: readonly DateTimeFormatOption[] = [
  {
    value: "browser",
    label: "Browser default",
    description: "Uses the locale and clock style configured in the browser.",
  },
  {
    value: "iso_local",
    label: "ISO-like local",
    description: "2026-08-09 17:45:30",
  },
  {
    value: "de_24h",
    label: "German 24-hour",
    description: "09.08.2026, 17:45:30",
  },
  {
    value: "us_12h",
    label: "US 12-hour",
    description: "Aug 9, 2026, 5:45 PM",
  },
  {
    value: "date_only",
    label: "Date only",
    description: "2026-08-09",
  },
];

const DATE_TIME_FORMAT_VALUES: ReadonlySet<string> = new Set<string>(
  DATE_TIME_FORMAT_OPTIONS.map((option: DateTimeFormatOption): DateTimeFormatPreference => option.value),
);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readNullableString(value: unknown, fieldName: string): string | null {
  if (value === null) {
    return null;
  }
  if (typeof value !== "string") {
    throw new Error(`User settings response field ${fieldName} is invalid`);
  }
  return value;
}

function readUserSettings(payload: unknown): UserSettings {
  if (!isRecord(payload) || typeof payload.dateTimeFormat !== "string" || typeof payload.dashboardDefaultPreset !== "string" || typeof payload.dashboardDefaultResolution !== "string") {
    throw new Error("User settings response is invalid");
  }
  if (!DATE_TIME_FORMAT_VALUES.has(payload.dateTimeFormat)) {
    throw new Error("User settings response field dateTimeFormat is invalid");
  }
  return {
    dateTimeFormat: payload.dateTimeFormat as DateTimeFormatPreference,
    dashboardDefaultPreset: payload.dashboardDefaultPreset as DashboardDefaultPreset,
    dashboardDefaultResolution: payload.dashboardDefaultResolution,
    updatedAt: readNullableString(payload.updatedAt, "updatedAt"),
  };
}

export async function loadUserSettings(): Promise<UserSettings> {
  return readUserSettings(await requestJson("/api/v1/user/settings"));
}

export async function saveUserSettings(
  dateTimeFormat: DateTimeFormatPreference,
  dashboardDefaultPreset: DashboardDefaultPreset,
  dashboardDefaultResolution: string,
): Promise<UserSettings> {
  return readUserSettings(
    await requestJson("/api/v1/user/settings", {
      method: "PATCH",
      body: JSON.stringify({ dateTimeFormat, dashboardDefaultPreset, dashboardDefaultResolution }),
    }),
  );
}
